import { useAbility } from "@casl/react";
import { useQueryClient } from "@tanstack/react-query";
import type { FC } from "react";

import { type AppAbility, collectionEventChannel, taskAssigneeEventChannel } from "@recobra/core";

import { useDocumentEvents } from "../documents";
import { taskCatalogQueryKey } from "../tasks/task-data";

export const CreationEventInvalidation: FC<{
  user: { userId: string; username: string };
}> = ({ user }) => {
  const queryClient = useQueryClient();
  const ability = useAbility<AppAbility>();
  const readsAllTasks = ability
    .rulesFor("read", "Task")
    .some((rule) => rule.conditions === undefined);
  const readsAllClients = ability
    .rulesFor("read", "Client")
    .some((rule) => rule.conditions === undefined);
  const readsAllCases = ability
    .rulesFor("read", "Case")
    .some((rule) => rule.conditions === undefined);

  useDocumentEvents(readsAllTasks ? collectionEventChannel("tasks") : undefined, () => {
    void queryClient.invalidateQueries({ queryKey: ["tasks", "list"] });
  });
  useDocumentEvents(ability.can("read", "Task") ? "settings/tasks" : undefined, () => {
    void queryClient.invalidateQueries({
      queryKey: taskCatalogQueryKey,
      refetchType: queryClient.isMutating({ mutationKey: taskCatalogQueryKey }) ? "none" : "active",
    });
  });
  useDocumentEvents(readsAllClients ? collectionEventChannel("clients") : undefined, () => {
    void queryClient.invalidateQueries({ queryKey: ["clients", "list"] });
    void queryClient.invalidateQueries({ queryKey: ["clients", "picker"] });
    void queryClient.invalidateQueries({ queryKey: ["clients", "all"] });
  });
  useDocumentEvents(readsAllCases ? collectionEventChannel("cases") : undefined, () => {
    void queryClient.invalidateQueries({ queryKey: ["cases", "list"] });
    void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  });

  useDocumentEvents(taskAssigneeEventChannel(user.userId), () => {
    void queryClient.invalidateQueries({ queryKey: ["tasks", "assigned", user.username] });
    void queryClient.invalidateQueries({ queryKey: ["tasks", "case"] });
    if (!readsAllTasks) {
      void queryClient.invalidateQueries({ queryKey: ["tasks", "list"] });
    }
    if (ability.can("read", "Case")) {
      void queryClient.invalidateQueries({ queryKey: ["cases", "list"] });
    }
  });

  return null;
};
